import { theme } from '@/styles/theme';
import { z } from 'zod';
import Cookies from 'js-cookie';
/**
 * Get user information from the user cookie
 * @returns User object with id, username, name, and email
 */
export const getUserFromCookie = () => {
    try {
        const userCookie = Cookies.get('user');
        if (userCookie) {
            const userObj = JSON.parse(userCookie);
            return {
                id: userObj?.id || userObj?.user_id || '',
                username: userObj?.username || '',
                name: userObj?.userAttributes?.name || '',
                email: userObj?.userAttributes?.email || ''
            };
        }
    }
    catch (error) {
        console.error('Error parsing user cookie:', error);
    }
    return { id: '', username: '', name: '', email: '' };
};
export function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });
}
export const isAdminPolicy = () => {
    const policy = JSON.parse(sessionStorage.getItem('policy') ?? '[]');
    if (policy.length === 0)
        return false;
    return policy.some((item) => item.name === 'Admin');
};
const httpUrlSchema = z.string().refine((url) => {
    try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    }
    catch {
        return false;
    }
}, { message: 'Invalid URL or URL must use HTTP or HTTPS protocol' });
export const isValidHttpUrl = (url) => {
    return httpUrlSchema.safeParse(url).success;
};
const extractActionsFromPolicy = (policy) => {
    const actions = [];
    if (policy.resource_action && Array.isArray(policy.resource_action)) {
        // resource_action contains objects with action arrays
        for (const resourceAction of policy.resource_action) {
            if (resourceAction.action && Array.isArray(resourceAction.action)) {
                actions.push(...resourceAction.action);
            }
        }
    }
    return actions;
};
const addActionsToSet = (actions, effect, allowedActions, deniedActions) => {
    if (effect === 'allow') {
        for (const act of actions)
            allowedActions.add(act);
    }
    else if (effect === 'deny') {
        for (const act of actions)
            deniedActions.add(act);
    }
};
export const isAuthorized = (action) => {
    try {
        // Get policies from sessionStorage
        const policies = JSON.parse(sessionStorage.getItem('policy') ?? '[]');
        if (policies.length === 0) {
            return false;
        }
        // Separate allowed and denied actions
        const allowedActions = new Set();
        const deniedActions = new Set();
        // Process each policy
        for (const policy of policies) {
            try {
                // Extract actions from resource_action array
                const actions = extractActionsFromPolicy(policy);
                // Add actions to appropriate set based on effect
                const effect = policy.effect?.toLowerCase();
                addActionsToSet(actions, effect, allowedActions, deniedActions);
            }
            catch (error) {
                console.warn('Failed to process policy:', error);
            }
        }
        // Check authorization: action must be explicitly allowed and not denied
        return allowedActions.has(action) && !deniedActions.has(action);
    }
    catch (error) {
        console.error('Error in isAuthorized function:', error);
        return false;
    }
};
export const normalizeEmail = (email) => {
    if (!email?.includes('@')) {
        throw new Error('Invalid email format');
    }
    // If it's already a standard email (e.g. abc@), return as is
    const excludeEmail = 'identity.deloitte';
    if (!email.endsWith(excludeEmail)) {
        return email;
    }
    // Otherwise, clean up Deloitte identity-style emails
    const localPart = email.split('@')[0];
    const lastUnderscoreIndex = localPart.lastIndexOf('_');
    if (lastUnderscoreIndex === -1) {
        throw new Error("Email format does not contain expected '_' separator");
    }
    const name = localPart.substring(0, lastUnderscoreIndex);
    let domainPart = localPart.substring(lastUnderscoreIndex + 1);
    // Ensure domain has '.com' if missing
    if (!domainPart.includes('.')) {
        domainPart += '.com';
    }
    return `${name}@${domainPart}`;
};
export const getSelectedLanguageFromCookie = () => {
    try {
        const userCookie = Cookies.get('user');
        if (!userCookie) {
            return 'en';
        }
        const user = JSON.parse(userCookie);
        const selectedLanguage = user?.details?.selected_language;
        if (selectedLanguage && typeof selectedLanguage === 'string') {
            return selectedLanguage.trim().toLowerCase();
        }
        return 'en';
    }
    catch (error) {
        console.error('Error reading selected_language from cookie:', error);
        return 'en';
    }
};
export const SETTINGS_VALIDATION = {
    nameAllowedChars: /^[A-Za-z .-]+$/,
    nameHasLetter: /[A-Za-z]/,
    maxNameLength: 50,
    reloadDelay: 100
};
/**
 * Lighten a hex color by an amount (0 to 1).
 * @param {string} hex - The hex color, e.g., "#26890D"
 * @param {number} amount - How much to lighten (0.2 = 20% lighter)
 * @returns {string} The new hex color
 */
export const getLightColorCode = (hex = theme.palette.primary.dark, amount = 0.4) => {
    // Remove hash
    hex = hex?.replace('#', '');
    // Parse r,g,b
    let r = Number.parseInt(hex.substring(0, 2), 16);
    let g = Number.parseInt(hex.substring(2, 4), 16);
    let b = Number.parseInt(hex.substring(4, 6), 16);
    // Calculate new values
    r = Math.round(r + (255 - r) * amount);
    g = Math.round(g + (255 - g) * amount);
    b = Math.round(b + (255 - b) * amount);
    // Ensure stays in range and convert back to hex
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
export const getDefaultApplication = () => {
    return {
        appName: 'ConvergeExperience',
        appId: '0' // Instance of env
    };
};
export const isGenericGreenColor = (color) => {
    return color === '#26890D';
};
export function arrayBufferToBase64(buffer) {
    if (!buffer)
        return null;
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCodePoint(bytes[i]);
    }
    return btoa(binary);
}
export const getEmailFromSession = () => {
    const sessionObj = sessionStorage.getItem('current_application');
    if (sessionObj) {
        return JSON.parse(sessionObj).details?.contactEmail || 'convergeconsumer@deloitte.com';
    }
    return 'convergeconsumer@deloitte.com';
};
